/**
 * Tooltip primitive (item #14 Phase 1, also items #16, #17 Phase 2).
 *
 * Wraps @radix-ui/react-tooltip with:
 *  - delayDuration 400ms (Goldilocks - not jumpy, not slow)
 *  - skipDelayDuration 0 (once one tooltip is open, neighbors open instantly)
 *  - Built-in variants: 'default' | 'shortcut' | 'rich'
 *  - Z-index from theme tier (--z-tooltip = 50)
 *  - Viewport-collision aware (Radix handles flip/shift automatically)
 *
 * Replaces native HTML title= attributes across the app. Solves Rick's
 * nav-rail-tooltip-overlap screenshot bug.
 *
 * Usage:
 *   <Tooltip content="Calendar"><button>...</button></Tooltip>
 *   <Tooltip content="Send" shortcut="⌘ Enter"><button>...</button></Tooltip>
 *   <Tooltip content={<div>Multi-line rich content</div>} variant="rich"><...></Tooltip>
 */
import * as React from 'react';
import * as TooltipPrimitive from '@radix-ui/react-tooltip';
import { cn } from '../../lib/utils.js';

export interface TooltipProps {
  /** The element that triggers the tooltip on hover/focus. */
  children: React.ReactNode;
  /** Tooltip body. String for simple text; ReactNode for rich content. */
  content: React.ReactNode;
  /** Optional keyboard shortcut hint (renders as <kbd> on the right). */
  shortcut?: string;
  /** 'default' | 'shortcut' | 'rich' - controls styling + padding. */
  variant?: 'default' | 'shortcut' | 'rich';
  /** Override placement (default 'top'). */
  side?: 'top' | 'right' | 'bottom' | 'left';
  /** Override offset from trigger (default 6). */
  sideOffset?: number;
  /** Override delay in ms (default 400). */
  delayDuration?: number;
  /** Force open (controlled). */
  open?: boolean;
  /** Disable the tooltip entirely (skip render). */
  disabled?: boolean;
  /** asChild forwards ref to child element (default true). */
  asChild?: boolean;
  className?: string;
}

/**
 * Global TooltipProvider should wrap the app once at root. Without it,
 * each Tooltip creates its own provider (still works, no shared delay).
 */
export const TooltipProvider = TooltipPrimitive.Provider;

export function Tooltip({
  children,
  content,
  shortcut,
  variant = 'default',
  side = 'top',
  sideOffset = 6,
  delayDuration = 400,
  open,
  disabled,
  asChild = true,
  className,
}: TooltipProps) {
  if (disabled || (!content && !shortcut)) return <>{children}</>;

  const isRich = variant === 'rich';
  const hasShortcut = !!shortcut || variant === 'shortcut';

  return (
    <TooltipPrimitive.Root delayDuration={delayDuration} open={open}>
      <TooltipPrimitive.Trigger asChild={asChild}>{children}</TooltipPrimitive.Trigger>
      <TooltipPrimitive.Portal>
        <TooltipPrimitive.Content
          side={side}
          sideOffset={sideOffset}
          collisionPadding={8}
          className={cn(
            'z-tooltip',
            'animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95',
            'data-[side=bottom]:slide-in-from-top-1 data-[side=left]:slide-in-from-right-1 data-[side=right]:slide-in-from-left-1 data-[side=top]:slide-in-from-bottom-1',
            'rounded-md shadow-lg',
            'bg-ink text-surface',
            'dark:bg-surface-raised dark:text-ink dark:border dark:border-line-strong',
            isRich ? 'px-3 py-2 text-sm max-w-xs leading-snug' : 'px-2 py-1 text-xs',
            hasShortcut && 'flex items-center gap-2',
            className
          )}
        >
          <span className={cn(hasShortcut && 'flex-1')}>{content}</span>
          {shortcut && (
            <kbd className="px-1 py-0.5 rounded text-[10px] font-mono bg-white/15 dark:bg-black/30 text-white dark:text-ink-muted">
              {shortcut}
            </kbd>
          )}
        </TooltipPrimitive.Content>
      </TooltipPrimitive.Portal>
    </TooltipPrimitive.Root>
  );
}
